import React, {useEffect, useRef, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {AppLocale} from '../../i18n/i18n.types';
import {useI18n} from '../../i18n/useI18n';
import {buildLocalizedSitePath} from '../../i18n/siteLocalePath';
import {ChevronDown, Globe} from 'lucide-react';
import styles from './styles/SiteHeader.module.css';

interface LanguageSwitcherProps {
    className?: string;
}

const locales: AppLocale[] = ['zh-CN', 'zh-TW', 'en-US', 'ko-KR', 'ja-JP', 'fr-FR', 'es-ES'];
const localeLabels: Record<AppLocale, string> = {
    'zh-CN': '简体中文',
    'zh-TW': '繁體中文',
    'en-US': 'English',
    'ko-KR': '한국어',
    'ja-JP': '日本語',
    'fr-FR': 'Français',
    'es-ES': 'Español'
};

const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({className}) => {
    const {locale, setLocale} = useI18n();
    const location = useLocation();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <div className={className} ref={containerRef}>
            <button
                aria-expanded={open}
                aria-haspopup="true"
                onClick={() => setOpen(v => !v)}
                type="button"
            >
                <Globe size={13}/>
                <span>{localeLabels[locale]}</span>
                <ChevronDown size={13}/>
            </button>
            {open && (
                <div className={styles.languageDropdown}>
                    {locales.map(item => (
                        <button
                            aria-pressed={locale === item}
                            key={item}
                            onClick={() => {
                                setLocale(item);
                                navigate(buildLocalizedSitePath(`${location.pathname}${location.search}${location.hash}`, item), {replace: true});
                                setOpen(false);
                            }}
                            type="button"
                        >
                            {localeLabels[item]}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

export default LanguageSwitcher;
